import { toast } from '@components/Toast';
import env from '@config/env';
import { ROUTE_PATHS, isAddNewBusinessNavigation } from '@config/routes';
import BusinessDetailPageSkeleton from '@loaders/BusinessDetailPageSkeleton';
import OnboardingPageLayout from '@modules/merchant/authentication/components/OnboardingPageLayout';
import BusinessDetailAbout from '@modules/merchant/onboarding/components/BusinessDetailAbout';
import BusinessDetailActions from '@modules/merchant/onboarding/components/BusinessDetailActions';
import BusinessDetailHero from '@modules/merchant/onboarding/components/BusinessDetailHero';
import BusinessDetailMeta from '@modules/merchant/onboarding/components/BusinessDetailMeta';
import OnboardingBackLink from '@modules/merchant/onboarding/components/OnboardingBackLink';
import { fetchAppConfig } from '@redux/merchant/merchantSlice';
import {
  clearSelectedBusiness,
  setBusinessSearchQuery,
  setSelectedBusiness,
  submitBusinessClaim,
} from '@redux/onboarding/onboardingSlice';
import { merchantAuthApi } from '@services/api';
import {
  extractPlaceCoverImage,
  extractPlaceDetailsResult,
  getPlaceAutocompleteStatus,
  mapPlaceDetails,
} from '@services/placesApi';
import { getApiErrorMessage } from '@utils/apiError';
import { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate } from 'react-router';

const BusinessDetailPage = memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAddingNewBusiness = isAddNewBusinessNavigation(location.state);
  const business = useSelector((state) => state.onboarding.businessSearch.selectedBusiness);
  const placeId = business?.placeId || business?.id;
  const detailsLoaded = Boolean(business?.detailsLoaded);
  const [isLoadingDetails, setIsLoadingDetails] = useState(() => Boolean(placeId) && !detailsLoaded);
  const [isClaiming, setIsClaiming] = useState(false);

  useEffect(() => {
    document.title = `Business Details | ${env.APP_NAME}`;
  }, []);

  useEffect(() => {
    if (!placeId || detailsLoaded) {
      setIsLoadingDetails(false);
      return undefined;
    }

    let cancelled = false;

    const loadDetails = async () => {
      setIsLoadingDetails(true);

      try {
        const response = await merchantAuthApi.placeDetail({ placeId });
        const status = getPlaceAutocompleteStatus(response);
        const result = extractPlaceDetailsResult(response);
        const coverImage = extractPlaceCoverImage(response);

        if ((status && status !== 'OK') || !result) {
          throw new Error(
            response?.data?.error_message ||
              response?.data?.message ||
              `Place details failed (${status || 'UNKNOWN'}).`,
          );
        }

        if (cancelled) return;

        dispatch(setSelectedBusiness(mapPlaceDetails(result, business, coverImage)));
      } catch (error) {
        if (cancelled) return;

        toast.error(getApiErrorMessage(error, 'Failed to load business details'));
        dispatch(clearSelectedBusiness());
        navigate(ROUTE_PATHS.SEARCH_BUSINESS, {
          replace: true,
          state: isAddingNewBusiness ? location.state : undefined,
        });
      } finally {
        if (!cancelled) setIsLoadingDetails(false);
      }
    };

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [business, detailsLoaded, dispatch, isAddingNewBusiness, location.state, navigate, placeId]);

  const handleSearchAnother = useCallback(() => {
    dispatch(clearSelectedBusiness());
    dispatch(setBusinessSearchQuery(''));
    navigate(ROUTE_PATHS.SEARCH_BUSINESS, { state: location.state });
  }, [dispatch, location.state, navigate]);

  const handleClaimBusiness = useCallback(async () => {
    if (!placeId || isClaiming) return;

    setIsClaiming(true);
    try {
      await dispatch(
        submitBusinessClaim({
          placeId,
          name: business?.name || '',
          address: business?.address || '',
          photo: business?.image || business?.photo || '',
        }),
      ).unwrap();
      await dispatch(fetchAppConfig());
      // Setup-guide / add-business claim returns to Setup Guide; first-time onboarding
      // still lands on the request-submitted confirmation screen.
      navigate(isAddingNewBusiness ? ROUTE_PATHS.SETUP_GUIDE : ROUTE_PATHS.REQUEST_SUBMITTED, {
        replace: true,
      });
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to claim business');
    } finally {
      setIsClaiming(false);
    }
  }, [business, dispatch, isAddingNewBusiness, isClaiming, navigate, placeId]);

  if (!business) {
    return (
      <Navigate
        to={ROUTE_PATHS.SEARCH_BUSINESS}
        replace
        state={isAddingNewBusiness ? location.state : undefined}
      />
    );
  }

  if (isLoadingDetails) {
    return <BusinessDetailPageSkeleton />;
  }

  return (
    <OnboardingPageLayout>
      <div className="flex w-full flex-col gap-form">
        <OnboardingBackLink
          to={ROUTE_PATHS.SEARCH_BUSINESS}
          state={isAddingNewBusiness ? location.state : undefined}
        />

        <div className="flex w-full flex-col gap-page">
          <BusinessDetailHero src={business.image} alt={business.name} />

          <div className="flex w-full flex-col gap-business-detail-section">
            <h1 className="desktop:text-[24px] text-[20px] font-semibold leading-6 desktop:leading-8 text-merchant-text-primary">
              {business.name}
            </h1>

            <BusinessDetailMeta
              category={business.category}
              isOpen={business.isOpen}
              closingTime={business.closingTime}
              rating={business.rating}
              reviewCount={business.reviewCount}
              priceLevel={business.priceLevel}
              address={business.address}
            />

            {business.about ? <BusinessDetailAbout about={business.about} /> : null}
          </div>

          <BusinessDetailActions
            onSearchAnother={handleSearchAnother}
            onClaim={handleClaimBusiness}
            claimDisabled={isClaiming}
            claimLabel={isClaiming ? 'Claiming...' : 'Claim this Business'}
          />
        </div>
      </div>
    </OnboardingPageLayout>
  );
});

BusinessDetailPage.displayName = 'BusinessDetailPage';

export default BusinessDetailPage;
